import { Avatar, Box, Button, Dialog, DialogContent, Divider, Grid, IconButton, Stack, styled, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonButton from "../../common/components/CommonButton";
import theme from "../../theme";
import CommonCard from "../../common/components/CommonCard";
import { ChatBubbleOutlineOutlined, Close, FiberManualRecord, Star, WatchLater } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { mentorsByStudent } from "../../api/mentorApi";
import CommonBookSession from "../../common/components/CommonBookSession";
import { confirmBooking, getEventByInvite } from "../../api/sessionApi";
import { ExpierienceYears, ServiceOffer, UserTypeId } from "../../common/const";
import moment from "moment";
import { joinConversation } from "../../api/chatApi";

const CoachType = {
    Assign: 'Assigned Coach',
    AI: 'DCP AI Coach'
}

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const Tutor = ({
    testType,
    assigned,
    coachData,
    setCoachData,
    mentorId
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const [coachType, setCoachType] = useState(CoachType.Assign);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [open, setOpen] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [sessionNote, setSessionNote] = useState('');

    const getTutorData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await mentorsByStudent(user?.student_id);
            const data = response.data.find((a) => a.mentor_profile_id === mentorId);
            loadImageAsBlob(data?.mentor?.user?.avatar);
            setCoachData(data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const loadImageAsBlob = async (avatarPath) => {
        try {
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${avatarPath}&download=false`;
            const response = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPreviewUrl(objectUrl);
        } catch (error) {
            console.error('Error loading image:', error);
        }
    };


    useEffect(() => {
        if (user?.student_id && assigned !== 'DISCOVERY') {
            getTutorData();
        }
    }, [assigned]);

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);

    const handleCloseAll = () => {
        setBookSessionDialog(false);
        setSessionNote('');
        setEventData(null);
    };

    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "mentor_profile_id": coachData?.mentor_profile_id,
                "student_id": user?.student_id,
                "service_offers": [ServiceOffer.coach],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        assigned !== 'DISCOVERY' ? (
            <Fragment>
                <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                    <DialogContent sx={{ p: 4, position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseAll}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: '#6B7280',
                            }}
                        >
                            <Close />
                        </IconButton>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                            Confirm Your Session
                        </Typography>

                        {coachData && (
                            <>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        gap: 2,
                                        mb: 3,
                                    }}
                                >
                                    <Avatar src={previewUrl} sx={{ width: 60, height: 60 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {`${coachData?.mentor?.user?.first_name} ${coachData?.mentor?.user?.last_name ? coachData?.mentor?.user?.last_name : ''}`}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                                            {coachData?.mentor.degree}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Star sx={{ fontSize: 16, color: '#FFA500' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {coachData?.mentor?.rating}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                {coachData?.mentor?.review} Reviews
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                • {coachData?.mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === coachData?.mentor.experience_years)?.label} Exp` : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                backgroundColor: '#F9FAFB',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <img
                                                src='/Images/graycalendar.png'
                                                style={{
                                                    width: 20,
                                                    position: 'relative'
                                                }}
                                                alt=''
                                            />
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                    Date
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                backgroundColor: '#F9FAFB',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                    Time
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                                        Any specific question for this session
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="Write here"
                                        value={sessionNote}
                                        onChange={(e) => setSessionNote(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    />
                                </Box>

                                <Button
                                    fullWidth
                                    onClick={handleConfirmBooking}
                                    sx={{
                                        backgroundColor: '#1E3A8A',
                                        color: 'white',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        height: '48px',
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: '#1E40AF',
                                        },
                                    }}
                                >
                                    Confirm Booking
                                </Button>
                            </>
                        )}
                    </DialogContent>
                </StyledDialog>
                <CommonCard
                    paddingBottom="0px"
                    padding="0px"
                    borderRadius={2}
                    headerContent={
                        <Fragment>
                            <Box display="flex" justifyContent="space-between" padding={2} alignItems="center">
                                <Box mt={2} display="flex" gap={1}>
                                    <CommonButton
                                        label={CoachType.Assign}
                                        width="160px"
                                        fontWeight={600}
                                        height="35px"
                                        color={coachType === CoachType.Assign ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={coachType === CoachType.Assign ? theme.palette.primary.main : '#FAFAFA'}
                                        handleClick={() => setCoachType(CoachType.Assign)}
                                        marginBottom="0px"
                                    />
                                    <CommonButton
                                        label={CoachType.AI}
                                        width="150px"
                                        height="35px"
                                        fontWeight={600}
                                        color={coachType === CoachType.AI ? theme.palette.secondary.main : theme.palette.background.dark}
                                        background={coachType === CoachType.AI ? theme.palette.primary.main : '#FAFAFA'}
                                        handleClick={() => setCoachType(CoachType.AI)}
                                        marginBottom="0px"
                                    />
                                </Box>
                                {coachType === CoachType.Assign && (
                                    <Box
                                        sx={{
                                            background: '#C9F6E9',
                                            textAlign: 'center',
                                            height: 25,
                                            width: 60,
                                            borderRadius: 1,
                                            paddingTop: 0.3,
                                        }}
                                    >
                                        <Typography fontSize={13} fontWeight={600} color="#01875F">Active</Typography>
                                    </Box>
                                )}
                            </Box>
                            <Divider />
                        </Fragment>
                    }
                    content={
                        coachType === CoachType.Assign ? (
                            <Box p={2} display="flex" justifyContent="space-between">
                                <Box display="flex" gap={3} width="80%">
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '38%',
                                            minWidth: '120px',
                                            height: 'fit-content'
                                        }}
                                    >
                                        <img
                                            src={previewUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(`${coachData?.mentor?.user?.first_name} ${coachData?.mentor?.user?.last_name}`)}&background=FAFAFAf1&color=fff`}
                                            alt="profile"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '-7px',
                                                right: '-20px',
                                                backgroundColor: 'red',
                                                color: 'white',
                                                padding: '2px 10px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                zIndex: 10
                                            }}
                                        >
                                            Human
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography fontWeight={600} fontSize={20}>{`${coachData?.mentor?.user?.first_name} ${coachData?.mentor?.user?.last_name}`}</Typography>
                                        <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                                            <Typography color="secondary.main" sx={{ fontSize: 14 }}>
                                                {testType} Expert
                                            </Typography>
                                            <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                                            <Typography color="secondary.main" sx={{ fontSize: 14 }}>
                                                {coachData?.mentor?.degree}
                                            </Typography>
                                        </Stack>
                                        <Typography mt={0.5} color="background.dark">{coachData?.mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === coachData?.mentor.experience_years)?.label} Experience` : '-'}</Typography>
                                        <Typography mt={0.5} color="background.dark">{coachData?.mentor?.bio}</Typography>
                                        <Box display="flex" gap={1} mt={1}>
                                            {coachData?.mentor?.expertise.map((a, index) => (
                                                <Box key={index} sx={{ background: '#EFF6FF', p: '4px 16px', borderRadius: 2 }}>
                                                    <Typography color="secondary.main">{a}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box display="flex" gap={1}>
                                    <CommonButton
                                        label="Message"
                                        height="30px"
                                        width="120px"
                                        color="white"
                                        background={theme.palette.secondary.main}
                                        startIcon={<ChatBubbleOutlineOutlined style={{ fontSize: '15px' }} />}
                                        fontWeight={600}
                                        handleClick={async () => {
                                            try {
                                                dispatch(setLoading({
                                                    loading: true
                                                }));
                                                const response = await joinConversation({
                                                    "student_id": user?.user_id,
                                                    "mentor_id": coachData?.mentor?.user_id
                                                });
                                                if (response.success) {
                                                    navigate(`/chat?id=${coachData?.mentor?.user_id}`)
                                                    dispatch(setLoading({
                                                        loading: false
                                                    }));
                                                }
                                            } catch (err) {
                                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                showNotification(errorMessage, 'error');
                                                dispatch(setLoading({
                                                    loading: false
                                                }));
                                            }
                                        }}
                                    />
                                    <CommonBookSession
                                        bookingUrl={process.env.REACT_APP_CALENDLY_SESSION_URL}
                                        open={open}
                                        setOpen={setOpen}
                                        studentName={`${user?.first_name} ${user?.last_name}`}
                                        email={user?.email}
                                        tutorName={`${coachData?.mentor?.user?.first_name || ''} ${coachData?.mentor?.user?.last_name || ''}`.trim()}
                                        mentorProfileId={coachData?.mentor_profile_id}
                                        handleClose={(data) => {
                                            setOpen(false);
                                            setInitialData(data);
                                        }}
                                    />
                                    {user?.user_type_id === UserTypeId.Student &&
                                        (<CommonButton
                                            label="Book a Session"
                                            height="30px"
                                            width="150px"
                                            fontWeight={600}
                                            handleClick={() => {
                                                setOpen(true)
                                            }}
                                        />)}
                                </Box>
                            </Box>
                        ) : (
                            <Box p={2} display="flex" justifyContent="space-between">
                                <Box display="flex" gap={3} width="80%">
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '10%',
                                            minWidth: '120px',
                                            height: 'fit-content'
                                        }}
                                    >
                                        <img
                                            src="/Images/customersupport.png"
                                            alt="profile"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                                borderRadius: '8px',
                                                border: '1px solid lightgray'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '-7px',
                                                right: '-15px',
                                                backgroundColor: 'red',
                                                color: 'white',
                                                padding: '2px 10px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                zIndex: 10
                                            }}
                                        >
                                            AI
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography fontWeight={600} fontSize={20}>DCP AI Coach</Typography>
                                        <Typography mt={0.5} mb={4} color="background.dark">Your personal AI teacher that helps you learn, practice, and understand any topic</Typography>
                                        <CommonButton
                                            label="Chat"
                                            height="30px"
                                            width="80px"
                                            color="white"
                                            background={theme.palette.secondary.main}
                                            marginBottom="0px"
                                            startIcon={<ChatBubbleOutlineOutlined style={{ fontSize: '15px' }} />}
                                            handleClick={() => navigate('/ai-Chat?type=coach')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )
                    }
                />
            </Fragment>
        ) : (
            <Box
                sx={{
                    mt: 2,
                    height: '30vh',
                    background: '#7A869126',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2
                }}
            >
                <Typography color={theme.palette.background.dark}>You have not taken DCP SAT/ACT Service</Typography>
                {user?.user_type_id === UserTypeId.Student && (
                    <CommonButton
                        label="Find a coach"
                        fontWeight={600}
                        width="150px"
                        marginTop={1.5}
                        handleClick={() => navigate('/book-session?type=coach')}
                    />)}
            </Box>
        )
    )
}

export default Tutor;